import React, { createElement } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
import { getLogoSrc } from './Logo';
import { SERVICE_CITIES } from '../data/cityMaterialContent';
import { GOOGLE_BUSINESS_PROFILE_URL } from '../utils/localBusiness';

// Cities surfaced in the footer. Every page links to these, which is the
// cheapest way to push crawl equity into the city landing pages — the rest
// remain reachable via /service-areas.
const FOOTER_CITY_SLUGS = ['seattle', 'tacoma', 'bellevue', 'redmond', 'renton', 'burien'];
export function Footer() {
  const t = useT();
  const footerCities = FOOTER_CITY_SLUGS.map(slug => SERVICE_CITIES.find(c => c.slug === slug)).filter(Boolean) as (typeof SERVICE_CITIES)[number][];
  return <footer className="pt-12 transition-colors duration-300 bg-[#0B1412]" style={{
    backgroundColor: "#00170A",
    backgroundImage: "none"
  }} data-id="element-870" data-mp="3qfjiuiukrhp6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-871" data-mp="2eyt8cnj4c3pr">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-8 mb-8 border-b border-white/20 pb-8" data-id="element-872" data-mp="2evxaez79tf1b">
          <div className="md:col-span-2" data-id="element-873" data-mp="3lbdbmpwng3k8">
            <Link to="/" className="inline-block mb-4" data-id="element-874" data-mp="3gbin2t0cjq2c">
              {/* Same artwork as the navbar, inverted so the black mark reads
                  white. `lighten` drops the inverted plate out against the
                  dark footer, leaving just the mark. */}
              <img src={getLogoSrc()} alt="206Gravel" className="block h-[42px] w-auto" style={{
              filter: 'invert(1)',
              mixBlendMode: 'lighten'
            }} data-id="element-875" data-mp="82qsptubnvu9" />
            </Link>
            <p className="text-sm font-medium leading-relaxed max-w-sm text-[#ffffff]" style={{
            color: "#F2EFE8"
          }} data-id="element-876" data-mp="hsk3wtqjy8xz">
              {t('footer.tagline')}
            </p>
          </div>

          <div data-id="element-877" data-mp="qt8h5m84g59t">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-878" data-mp="qsqea6qyessx">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#cccccc]" data-id="element-879" data-mp="mt7vbgip0rsq">
              <li data-id="element-880" data-mp="tvw7cfth7dfg">
                <a href="tel:206-650-0129" className="hover:text-white/70 transition-colors" data-id="element-881" data-mp="twfmxpvnlpqb">
                  (206) 650-0129
                </a>
              </li>
              <li data-id="element-882" data-mp="13sr4aua3p5rm">
                <a href="mailto:info@206gravel.com" className="hover:text-white/70 transition-colors" data-id="element-883" data-mp="13s9ae4awv3mv">
                  info@206gravel.com
                </a>
              </li>
              <li data-id="element-884" data-mp="1cm43uf2o75mo">Bonney Lake, WA</li>
              <li data-id="element-885" data-mp="1cl2t6bpkgl5h">
                <a href={GOOGLE_BUSINESS_PROFILE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors" data-id="element-886" data-mp="1cnp0ca8joyn6">
                  Find us on Google
                </a>
              </li>
            </ul>
          </div>

          <div data-id="element-887" data-mp="1rj4z2sk2lipw">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-888" data-mp="1rhk4x41o41sk">
              Service Areas
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#cccccc]" data-id="element-889" data-mp="3oczqrf0zmp85">
              {footerCities.map(city => <li key={city.slug} data-id="element-890" data-mp="3tai036ix36go">
                  <Link to={`/gravel-delivery/${city.slug}`} className="hover:text-white/70 transition-colors" data-id="element-891" data-mp="2aevf3w32tu7d">
                    Gravel Delivery {city.name}
                  </Link>
                </li>)}
              <li data-id="element-892" data-mp="21mjyf1xrll4l">
                <Link to="/service-areas" className="hover:text-white/70 transition-colors" data-id="element-893" data-mp="21nv4j8eon4yv">
                  All service areas
                </Link>
              </li>
            </ul>
          </div>

          <div data-id="element-894" data-mp="2tcy80lyqwbag">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-895" data-mp="2tdg1jau0wbeo">
              Products
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#cccccc]" data-id="element-896" data-mp="2pd5vjy68dkzt">
              <li data-id="element-897" data-mp="2kc02u0cklxqa"><Link to="/?category=Driveway%20%26%20Base#materials" className="hover:text-white/70 transition-colors" data-id="element-898" data-mp="2kc019vwx9po6">Driveway &amp; Base</Link></li>
              <li data-id="element-899" data-mp="18y4pmx587z1p"><Link to="/?category=Drainage%20%26%20Clear%20Rock#materials" className="hover:text-white/70 transition-colors" data-id="element-900" data-mp="18y4kyjua7avd">Drainage &amp; Clear Rock</Link></li>
              <li data-id="element-901" data-mp="13xqdb2wle2xv"><Link to="/?category=Soil%20%26%20Compost#materials" className="hover:text-white/70 transition-colors" data-id="element-902" data-mp="13xqev7c8qazz">Soil &amp; Compost</Link></li>
              <li data-id="element-903" data-mp="2wmzjiis06nw7"><Link to="/?category=Bark%20%26%20Ground%20Cover#materials" className="hover:text-white/70 transition-colors" data-id="element-904" data-mp="2wmzeu5h25zpv">Bark &amp; Ground Cover</Link></li>
              <li data-id="element-905" data-mp="2isyifidkwoso"><Link to="/?category=Sand%20%26%20Fill#materials" className="hover:text-white/70 transition-colors" data-id="element-906" data-mp="2it862zb5ijv7">Sand &amp; Fill</Link></li>
              <li data-id="element-907" data-mp="3bjgu37sdbyhp"><Link to="/?category=Large%20Rock%20%26%20Erosion%20Control#materials" className="hover:text-white/70 transition-colors" data-id="element-908" data-mp="3bjgpeuhfbabd">Large Rock &amp; Erosion Control</Link></li>
            </ul>
          </div>

          <div data-id="element-909" data-mp="31kr8p519jvwp">
            <h4 className="font-heading text-[#ffffff] font-bold mb-4" data-id="element-910" data-mp="31l13d5x96r8q">
              {t('footer.resources')}
            </h4>
            <ul className="space-y-3 text-sm font-medium text-[#cccccc]" data-id="element-911" data-mp="36mgmutjs8pp0">
              {/* Leads the list: it's the entry point for anyone who doesn't
                  yet know which material they need, so it precedes the
                  reference pages that assume they do. */}
              <li data-id="element-912" data-mp="1no27nnfumcc4">
                <Link to="/help" className="hover:text-white/70 transition-colors" data-id="element-913" data-mp="1imv0nqlj5h6i">
                  {t('footer.materialQuiz')}
                </Link>
              </li>
              <li data-id="element-914" data-mp="1wluo4uxb3q13">
                <Link to="/compare" className="hover:text-white/70 transition-colors" data-id="element-915" data-mp="1wk9s10vcfz14">
                  {t('nav.compareMaterials')}
                </Link>
              </li>
              <li data-id="element-916" data-mp="13yi3wrembuay">
                <Link to="/tons-to-yards" className="hover:text-white/70 transition-colors" data-id="element-917" data-mp="13vm2uu7h4pia">
                  Tons to Yards
                </Link>
              </li>
              <li data-id="element-918" data-mp="1dojpncji10mp">
                <Link to="/delivery-prep" className="hover:text-white/70 transition-colors" data-id="element-919" data-mp="1dp2zs72wqkq6">
                  {t('footer.deliveryPrep')}
                </Link>
              </li>
              <li data-id="element-920" data-mp="l65y1uoraaw2">
                <Link to="/delivery-zones" className="hover:text-white/70 transition-colors" data-id="element-921" data-mp="l5nt89o12ocj">
                  {t('nav.deliveryZones')}
                </Link>
              </li>
              <li data-id="element-922" data-mp="2g9u7fhsj1rq1">
                <Link to="/blog" className="hover:text-white/70 transition-colors" data-id="element-923" data-mp="2gadjwizernwo">
                  {t('footer.blog')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center text-sm font-medium text-white/60" data-id="element-924" data-mp="2q2ji4ag971kb">
          <p data-id="element-925" data-mp="1xbrguykbzmq4">
            &copy; {new Date().getFullYear()} 206Gravel LLC. {t('footer.rights')}
          </p>
          <div className="mt-4 md:mt-0 space-x-6" data-id="element-926" data-mp="22am72lkmodk9">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-927" data-mp="22be1vu6ei79h">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms-of-service" className="hover:text-white transition-colors" data-id="element-928" data-mp="2bbanfmxhuq0b">
              {t('footer.terms')}
            </Link>
            <Link to="/refund-policy" className="hover:text-white transition-colors" data-id="element-929" data-mp="3iocfg4is5nzp">
              {t('footer.refund')}
            </Link>
          </div>
        </div>
      </div>
    </footer>;
}